import {faPlus, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Form, FormInstance, Select, Tooltip} from 'antd';
import {useMemo} from 'react';

import {AlertFallback} from '../../../../../../../../../component/alert-fallback/alert-fallback';
import {useLocale} from '../../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../../provider/locale/localization';
import {useFormRules} from '../../../../../../../../../service/form/form-rules-hook';
import {useLinkBrands} from '../../../../../../../../../service/source-settings/link-brands/link-brands-hook';
import {LinkBrandType} from '../../../../../../../../../service/source-settings/link-brands/link-brands-type';
import {useAllStoreAccountApps} from '../../../../../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {handleFilterOption} from '../../../../../../../../../util/antd/select-helper';
import {APPSTORE_APPS_LIST_LIMIT} from '../add-appstore-account-const';
import {isLastAppstoreBrandAppsRowComplete} from '../add-appstore-form-validation-helper';

import {AppstoreAppsBrandItemEnum, AppstoreAppsFormEnum, AppstoreAppsFormType} from './appstore-apps-form-type';
import * as styles from './appstore-apps-form.scss';

type PropsType = {
    accountId: number;
    catalogGroupId: number;
    form: FormInstance<AppstoreAppsFormType>;
    formId: string;
    hideStepDescription?: boolean;
    initialValues?: AppstoreAppsFormType;
    linkExtraAppOptions?: Array<{appId: string; appName: string}>;
    onFinish: (values: AppstoreAppsFormType) => void;
};

export function AppstoreAppsForm(props: PropsType): JSX.Element {
    const {
        accountId,
        catalogGroupId,
        form,
        formId,
        hideStepDescription = false,
        initialValues,
        linkExtraAppOptions = [],
        onFinish,
    } = props;
    const {requiredFieldRule} = useFormRules();
    const {getLocalizedString} = useLocale();
    const {result, isInProgress, processError} = useLinkBrands(catalogGroupId);
    const brandOptions = useMemo<Array<LinkBrandType>>(() => result ?? [], [result]);

    const {
        data: appsListData,
        isLoading: isAppsListLoading,
        isError: isAppsListError,
    } = useAllStoreAccountApps(accountId, {limit: APPSTORE_APPS_LIST_LIMIT}, {enabled: accountId >= 1});

    const appOptions = useMemo(() => appsListData?.results ?? [], [appsListData?.results]);

    const extraAppOptions = useMemo(() => {
        const seen = new Set(appOptions.map((app) => app.appId));

        return linkExtraAppOptions.filter((opt) => !seen.has(opt.appId));
    }, [appOptions, linkExtraAppOptions]);

    const brandApps = Form.useWatch(AppstoreAppsFormEnum.brandApps, form);
    const canAddBrandRow = useMemo(() => isLastAppstoreBrandAppsRowComplete(brandApps), [brandApps]);

    const selectedAppsByRow = useMemo<Array<Map<string, string>>>(() => {
        if (!brandApps) {
            return [];
        }

        return brandApps.map((_row, rowIndex) => {
            const usedInOtherRows = new Map<string, string>();

            brandApps.forEach((row, index) => {
                if (index !== rowIndex) {
                    const brand = brandOptions.find((opt) => opt.id === row?.brandId);
                    const brandName = brand?.name ?? '';

                    row?.apps?.forEach((appId) => usedInOtherRows.set(appId, brandName));
                }
            });

            return usedInOtherRows;
        });
    }, [brandApps, brandOptions]);

    return (
        <Form<AppstoreAppsFormType>
            form={form}
            id={formId}
            initialValues={
                initialValues || {
                    [AppstoreAppsFormEnum.brandApps]: [{apps: []}],
                }
            }
            layout="vertical"
            onFinish={onFinish}
        >
            {!hideStepDescription && (
                <p className={styles.AppstoreAppsForm_description}>
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__STEP2__DESCRIPTION" />
                </p>
            )}

            <Form.List name={AppstoreAppsFormEnum.brandApps}>
                {(fields, {add, remove}) => (
                    <>
                        {fields.map(({key, name, ...restField}) => (
                            <div
                                className={
                                    fields.length > 1
                                        ? `${styles.AppstoreAppsForm_brandItem} ${styles.AppstoreAppsForm_brandItem_withRemove}`
                                        : styles.AppstoreAppsForm_brandItem
                                }
                                key={key}
                            >
                                {fields.length > 1 && (
                                    <button
                                        className={styles.AppstoreAppsForm_removeButton}
                                        onClick={() => remove(name)}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                )}
                                <Form.Item
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...restField}
                                    label={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__BRAND" />}
                                    name={[name, AppstoreAppsBrandItemEnum.brandId]}
                                    rules={[requiredFieldRule]}
                                >
                                    <Select<number>
                                        filterOption={handleFilterOption}
                                        loading={isInProgress}
                                        notFoundContent={processError ? <AlertFallback /> : null}
                                        placeholder={
                                            <Locale stringKey="SOURCE_SETTINGS__LINKS__LINK_MANAGEMENT__BRAND__SELECT__PLACEHOLDER" />
                                        }
                                        showSearch
                                    >
                                        {!processError &&
                                            brandOptions.map(
                                                (option: LinkBrandType): JSX.Element => (
                                                    <Select.Option key={option.id} value={option.id}>
                                                        {option.name}
                                                    </Select.Option>
                                                )
                                            )}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...restField}
                                    className={styles.AppstoreAppsForm_appsFormItem}
                                    label={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__APPS" />}
                                    name={[name, AppstoreAppsBrandItemEnum.apps]}
                                    rules={[requiredFieldRule]}
                                >
                                    <Select<Array<string>>
                                        filterOption={handleFilterOption}
                                        loading={isAppsListLoading}
                                        mode="multiple"
                                        notFoundContent={isAppsListError ? <AlertFallback /> : null}
                                        optionFilterProp="label"
                                        placeholder={getLocalizedString(
                                            'SOURCE_SETTINGS__ACCOUNTS__APPSTORE__APPS__PLACEHOLDER'
                                        )}
                                        showSearch
                                    >
                                        {appOptions.map((app) => {
                                            const conflictBrand = selectedAppsByRow[name]?.get(app.appId);

                                            return (
                                                <Select.Option
                                                    disabled={Boolean(conflictBrand)}
                                                    key={app.appId}
                                                    label={`${app.appName} ${app.appId}`}
                                                    value={app.appId}
                                                >
                                                    <Tooltip
                                                        title={
                                                            conflictBrand
                                                                ? getLocalizedString(
                                                                      'SOURCE_SETTINGS__ACCOUNTS__APPSTORE__APP__ALREADY_SELECTED',
                                                                      {brandName: conflictBrand}
                                                                  )
                                                                : null
                                                        }
                                                    >
                                                        {app.appName} ({app.appId})
                                                    </Tooltip>
                                                </Select.Option>
                                            );
                                        })}
                                        {extraAppOptions.map((app) => {
                                            const conflictBrand = selectedAppsByRow[name]?.get(app.appId);

                                            return (
                                                <Select.Option
                                                    disabled={Boolean(conflictBrand)}
                                                    key={`extra-${app.appId}`}
                                                    label={`${app.appName} ${app.appId}`}
                                                    value={app.appId}
                                                >
                                                    <Tooltip
                                                        title={
                                                            conflictBrand
                                                                ? getLocalizedString(
                                                                      'SOURCE_SETTINGS__ACCOUNTS__APPSTORE__APP__ALREADY_SELECTED',
                                                                      {brandName: conflictBrand}
                                                                  )
                                                                : null
                                                        }
                                                    >
                                                        {app.appName} ({app.appId})
                                                    </Tooltip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </div>
                        ))}

                        <button
                            className={styles.AppstoreAppsForm_addBrandButton}
                            disabled={!canAddBrandRow}
                            onClick={() => add({[AppstoreAppsBrandItemEnum.apps]: []})}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__ADD_BRAND" />
                        </button>
                    </>
                )}
            </Form.List>
        </Form>
    );
}
